import { computed, Injectable, signal } from '@angular/core';

import { APP_MESSAGES } from '@app/core/constants/app-messages';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastMessageState {
  readonly variant: ToastVariant;
  readonly title: string;
  readonly message: string;
  readonly duration: number;
  readonly dismissible: boolean;
  readonly showProgress: boolean;
}

export interface ToastQueueItem extends ToastMessageState {
  readonly id: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  // Keeps toast timing and progress behavior consistent across all variants.
  private static readonly TOAST_TIMEOUT_MS = 8000;
  // Zero duration keeps an error toast open until the user dismisses it.
  private static readonly PERSISTENT_TOAST_TIMEOUT_MS = 0;

  private toastSequence = 0;
  private readonly toastQueueState = signal<ToastQueueItem[]>([]);

  readonly toasts = this.toastQueueState.asReadonly();
  // Keeps backward compatibility for places that still read only the active toast.
  readonly toast = computed(() => this.toastQueueState()[0] ?? null);

  showErrorMessage(
    message: string,
    title: string = APP_MESSAGES.general.DEFAULT_ERROR_TITLE,
  ): void {
    this.show({
      variant: 'error',
      title,
      message,
      duration: NotificationService.PERSISTENT_TOAST_TIMEOUT_MS,
      dismissible: true,
      showProgress: false,
    });
  }

  showSuccessMessage(
    message: string,
    title: string = APP_MESSAGES.general.DEFAULT_SUCCESS_TITLE,
  ): void {
    this.show({
      variant: 'success',
      title,
      message,
      duration: NotificationService.TOAST_TIMEOUT_MS,
      dismissible: true,
      showProgress: true,
    });
  }

  showInfoMessage(message: string, title: string = APP_MESSAGES.general.DEFAULT_INFO_TITLE): void {
    this.show({
      variant: 'info',
      title,
      message,
      duration: NotificationService.PERSISTENT_TOAST_TIMEOUT_MS,
      dismissible: true,
      showProgress: false,
    });
  }

  showWarningMessage(
    message: string,
    title: string = APP_MESSAGES.general.DEFAULT_WARNING_TITLE,
  ): void {
    this.show({
      variant: 'warning',
      title,
      message,
      duration: NotificationService.TOAST_TIMEOUT_MS,
      dismissible: true,
      showProgress: true,
    });
  }

  show(toast: ToastMessageState): void {
    const nextToast: ToastQueueItem = {
      ...toast,
      id: this.createToastId(),
    };

    // Newest toast is prepended so it renders on top while older toasts stay visible below.
    // Error toasts are the exception: a new error replaces earlier ones so only the latest shows.
    this.toastQueueState.update((currentQueue) => [
      nextToast,
      ...(toast.variant === 'error'
        ? currentQueue.filter((queued) => queued.variant !== 'error')
        : currentQueue),
    ]);
  }

  dismiss(toastId?: string): void {
    if (!toastId) {
      this.toastQueueState.update((currentQueue) => currentQueue.slice(1));
      return;
    }

    this.toastQueueState.update((currentQueue) =>
      currentQueue.filter((toastMessage) => toastMessage.id !== toastId),
    );
  }

  // Builds a stable unique id per toast instance for list rendering and targeted dismissal.
  private createToastId(): string {
    this.toastSequence += 1;
    return `toast-${Date.now()}-${this.toastSequence}`;
  }
}
